import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { Observable, Subject, takeUntil } from 'rxjs';
import { Tooltip } from '../../models/Tooltip';
import { ValidateInputService } from '../../services/validate-input.service';
import { Constants } from '../../utilities/Constants';
import { datepickerAllowedChars } from '../../utilities/regex-patterns';
import { TransactionType } from '../../types/Transaction';
import { Store } from '@ngrx/store';
import { selectRecipientSelection } from '../../../create-request/reducers';
import { getProcessDateInIsoFormat } from '../../../create-request/components/transactions-table/table-helpers';
import { FormatDatePipe } from '../../pipes/FormatDatePipe';

@Component({
  standalone: false,
  selector: 'mm-date-picker',
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss']
})
export class DatePickerComponent implements OnInit {
  @Output() newValueEvent = new EventEmitter<string>();
  @Output() showSufficientFundsWarningEvent = new EventEmitter<boolean>();
  datepicker: any;
  datepickerAllowedChars = datepickerAllowedChars;
  @Input() label: string;
  @Input() minDate!: Date;
  @Input() maxDate!: Date;
  @Input() disableWeekends: boolean;
  @Input() dateValue: string;
  @Input() id: string;
  @Input() tooltip: Tooltip;
  @Input() controlValidation$: Observable<{ hasError: boolean, errorText: string }>;
  @Input() disableDateSelect: boolean;
  @Input() showIntlWireTooltip: boolean;
  @Input() showOptionalLabel: boolean;
  @Input() transactionType: TransactionType;
  @Input() formControlValue: string;
  showSufficientFundsWarning: boolean;
  wireRecipientParty: string;
  isPartyChanged: boolean = false;
  readonly intlWireTooltipMsg: string = Constants.wireIntlProcessDateToolTip;
  readonly processDateLabel: string = 'Process date';
  formatDatePipe = new FormatDatePipe();
  futureDatedSufficientFundsWarning: string = Constants.futureDatedSufficientFundsWarning;
  destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(public validationService: ValidateInputService, private store: Store, private readonly changeDetectorRef: ChangeDetectorRef) { }

  ngAfterViewInit(): void {
    this.datepicker = document.getElementById(this.id);
    this.datepicker.value = this.dateValue ?? ''; // If an initial value is given, set it as the value, otherwise empty string
    const today: Date = new Date();
    const yesterday: Date = today;
    yesterday.setDate(today.getDate() - 1);
    if (this.maxDate) {
      this.datepicker.calendarMaxDate = this.maxDate;
    }
    if (this.minDate) {
      this.datepicker.calendarMinDate = this.minDate;
    }
    this.datepicker.disabledDates = this.getDatesInRange(this.datepicker.calendarMinDate, yesterday);
    this.showSufficientFundWarningMessage();
    this.changeDetectorRef.detectChanges();
  }

  ngOnInit(): void {
    this.controlValidation$.pipe(takeUntil(this.destroy$)).subscribe(controlValidation => {
      this.datepicker.hasError = controlValidation.hasError;
      this.datepicker.errorText = controlValidation.errorText;
    });

    this.store.select(selectRecipientSelection).pipe(takeUntil(this.destroy$)).subscribe((party) => {
      if (party !== this.wireRecipientParty) {
          this.isPartyChanged = true;
          this.wireRecipientParty = party;
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.datepicker) {
      var defaultProcessDate = new Date(getProcessDateInIsoFormat(this.formControlValue));
      // Has to be written in ngOnChanges for the new value to reflected when the user updates the value of extra prompts
      // Check that dateValue is present on 'changes' otherwise selecting the same date twice will result in an empty string
      if (changes?.dateValue) {
        this.datepicker.value = changes.dateValue.currentValue ?? '';
        // Required to resolve Everest component error when date is auto-changed during cutoff period for wire SAC-303591
        if (this.datepicker.errorText === '' && this.transactionType === 'wire') { this.datepicker.hasError = false };

        if (new Date(this.datepicker.value) < this.minDate && this.transactionType === 'wire') {
          this.datepicker.hasError = true;
          this.datepicker.errorText = Constants.validationPastDateError;
        }

        this.showSufficientFundWarningMessage();
      } else if (this.transactionType === 'wire'
          && this.label === this.processDateLabel
          && this.isPartyChanged
          && this.minDate.toDateString() !== this.datepicker.calendarMinDate.toDateString()
          && defaultProcessDate.toDateString() === this.minDate.toDateString()) {
            if (this.maxDate) {
              this.datepicker.calendarMaxDate = this.maxDate;
            }
            if (this.minDate) {
              this.datepicker.calendarMinDate = this.minDate;
              // set datepicker.disabledDates
              const today: Date = new Date();
              const yesterday: Date = today;
              yesterday.setDate(today.getDate() - 1);
              this.datepicker.disabledDates = this.getDatesInRange(this.minDate, yesterday);
              // set datepicker.value
              var month = (this.minDate.getMonth()+1).toString();
              if (month.length != 2) {
                month = `0${month}`;
              }
              var date = this.minDate.getDate().toString();
              if (date.length != 2) {
                date = `0${date}`;
              }
              this.datepicker.value = `${month}/${date}/${this.minDate.getFullYear()}`;
              this.onValueChange();
            }
      }

    }
  }

  validateDateInputLength() {
    if (this.datepicker.value?.length > 10) {
      this.datepicker.value = this.datepicker.value.substring(0, 10);
    }
  }

  onValueChange(): void {
    this.datepicker.hasError = false;
    this.newValueEvent.emit(this.datepicker?.value);
  }

  getDatesInRange(startDate: Date, endDate: Date): Date[] {
    let dates: Date[] = [];
    let date: Date = startDate;
    while (startDate <= endDate) {
      dates.push(new Date(date));
      date.setDate(date.getDate() + 1);
    }
    return dates;
  }

  showSufficientFundWarningMessage(): void {
    var defaultProcessDate = new Date(getProcessDateInIsoFormat(this.formControlValue));
    if (this.transactionType === 'wire'
      && (this.formatDatePipe.transform(defaultProcessDate.toLocaleDateString()) !== this.formatDatePipe.transform(this.datepicker.value))
      && !(new Date(this.datepicker.value) < this.minDate)) {
      this.showSufficientFundsWarning = true;
      this.showSufficientFundsWarningEvent.emit(true);
    }
    else {
      this.showSufficientFundsWarning = false;
      this.showSufficientFundsWarningEvent.emit(false);
    }
  }
}
